import * as styles from "./DataTableHeaderCell.css";

import type { JSX } from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import {
  type Header,
  type RowData,
  type SortingState,
  flexRender,
} from "@tanstack/react-table";

import type {
  DataTableColumnFilter,
  DataTableSize,
  DataTableSpacing,
  DataTableVariant,
} from "./DataTable.types";
import { DataTableFilter } from "./DataTableFilter";
import { DataTableSortButton } from "./DataTableSortButton";
import { dataTableSpacingValue } from "./cellSizeStyles";
import type { dataTableFeatures } from "./dataTableFeatures";

interface DataTableHeaderCellProps<TData extends RowData> {
  header: Header<typeof dataTableFeatures, TData, unknown>;
  sorting: SortingState;
  columnFilters: DataTableColumnFilter[];
  onColumnFiltersChange?: (filters: DataTableColumnFilter[]) => void;
  onSortPrefetch?: (sorting: SortingState) => void;
  sortable: boolean;
  size: DataTableSize;
  variant: DataTableVariant;
  columnBorders?: boolean;
  sticky?: boolean;
  stickyInset?: string;
  opaque?: boolean;
  horizontalSpacing: DataTableSpacing;
  verticalSpacing: DataTableSpacing;
}

export function DataTableHeaderCell<TData extends RowData>({
  header,
  sorting,
  columnFilters,
  onColumnFiltersChange,
  onSortPrefetch,
  sortable,
  size,
  variant,
  columnBorders,
  sticky,
  stickyInset,
  opaque,
  horizontalSpacing,
  verticalSpacing,
}: DataTableHeaderCellProps<TData>): JSX.Element | null {
  if (header.isPlaceholder) {
    return null;
  }

  const canSort = sortable && header.column.getCanSort();

  const filterType = header.column.columnDef.meta?.filter;
  const columnId = header.column.id;
  const filter = columnFilters.find((entry) => entry.id === columnId);

  const setFilter = (next: DataTableColumnFilter | null) => {
    const others = columnFilters.filter((entry) => entry.id !== columnId);

    onColumnFiltersChange?.(next ? [...others, next] : others);
  };

  const sortEntry = sorting.find((entry) => entry.id === columnId);

  const sortDirection = sortEntry ? (sortEntry.desc ? "desc" : "asc") : false;

  const content = flexRender(
    header.column.columnDef.header,
    header.getContext(),
  );

  const label = <span className={styles.label}>{content}</span>;

  return (
    <div
      role="columnheader"
      className={styles.cell({
        size,
        variant,
        columnBorders,
        sorted: sortDirection !== false,
        sticky,
        opaque,
      })}
      style={{
        gridColumn: `span ${header.colSpan}`,
        left: sticky ? stickyInset : undefined,
        paddingInline: dataTableSpacingValue[horizontalSpacing],
        paddingBlock: dataTableSpacingValue[verticalSpacing],
      }}
      aria-sort={
        sortDirection === "asc"
          ? "ascending"
          : sortDirection === "desc"
            ? "descending"
            : undefined
      }
    >
      {canSort ? (
        <DataTableSortButton
          column={header.column}
          direction={sortDirection}
          label={label}
          onClick={header.column.getToggleSortingHandler()}
          onSortPrefetch={onSortPrefetch}
        />
      ) : false !== sortDirection ? (
        <>
          {label}

          <Icon
            name={"asc" === sortDirection ? "ChevronUp" : "ChevronDown"}
            size="sm"
            className={styles.sortIcon({ active: true })}
          />
        </>
      ) : (
        label
      )}

      {filterType && onColumnFiltersChange ? (
        <DataTableFilter
          columnId={columnId}
          label={typeof content === "string" ? content : columnId}
          spec={filterType}
          filter={filter}
          onChange={setFilter}
        />
      ) : null}
    </div>
  );
}
